import { Button, Link, Text, Title3, makeStyles } from "@fluentui/react-components";
import MarkdownPreview from "@uiw/react-markdown-preview";
import MDEditor, { commands } from "@uiw/react-md-editor";
import "@uiw/react-md-editor/markdown-editor.css";
import DOMPurify from "dompurify";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { DisclaimerMaxLength, DisclaimerPlaceholderText } from "../../common/commonConstants";
import { htmlToMarkdown, mdToHtml } from "../../common/htmlMarkdownHelper";
import * as diningReducer from "../../reducers/diningReducer";
import * as diningSelector from "../../selectors/diningSelector";
import { useAppDispatch } from "../../store/store";
import styles from "../../styles/resyncCafe.module.css";

const useStyles = makeStyles({
  wrapper: {
    marginTop: "10px",
    columnGap: "12px",
    display: "flex",
    justifyContent: "flex-end",
  },
});

const editorCommands = [commands.bold, commands.italic, commands.link];

const Disclaimer: React.FC = () => {
  const localStyles = useStyles();
  const dispatch = useAppDispatch();
  const selectedSyncCafe = useSelector(diningSelector.syncCafeSelector);
  const editedCafe = useSelector(diningSelector.editedSyncCafeSelector);
  const rawDisclaimer = editedCafe?.cafeDisclaimer?.trim() || "";
  const selectedCafeDisclaimer = rawDisclaimer === "" ? DisclaimerPlaceholderText : rawDisclaimer;
  const [isEditing, setIsEditing] = useState(false);
  const [debouncedDraft, setDebouncedDraft] = useState<string>("");
  const [statusMsg, setStatusMsg] = useState<string | null>(null);
  const [lastAction, setLastAction] = useState<"save" | "cancel" | null>(null);
  const [isInvalidContent, setIsInvalidContent] = useState(false);
  const [isContentUnchanged, setIsContentUnchanged] = useState(true);
  const editorRef = useRef<HTMLDivElement>(null);
  const isPlaceholderText = selectedCafeDisclaimer === DisclaimerPlaceholderText;

  function truncateHtmlPreserveFormatting(html: string, maxLength: number): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const resultDoc = document.implementation.createHTMLDocument("");
    const resultBody = resultDoc.body;
    let charCount = 0;
    function cloneAndTruncateNode(node: Node): Node | null {
      if (charCount >= maxLength) return null;
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent || "";
        const remaining = maxLength - charCount;
        const visible = text.slice(0, remaining);
        charCount += visible.length;
        return document.createTextNode(visible);
      }
      if (node.nodeType === Node.ELEMENT_NODE) {
        const clone = node.cloneNode(false) as HTMLElement;
        for (let child = node.firstChild; child; child = child.nextSibling) {
          const clonedChild = cloneAndTruncateNode(child);
          if (clonedChild) {
            clone.appendChild(clonedChild);
          }
          if (charCount >= maxLength) break;
        }
        return clone;
      }
      return null;
    }
    const truncated = cloneAndTruncateNode(doc.body);
    if (truncated) resultBody.appendChild(truncated);
    return resultBody.innerHTML;
  }

  const normalizeMarkdown = (markdown: string) => {
    const safeMarkdown = markdown ?? "";
    const html = mdToHtml(safeMarkdown);
    const doc = new DOMParser().parseFromString(html, "text/html");
    const plainText = doc.body.textContent || "";
    if (plainText.trim() === "") return "";

    const truncatedHtml = truncateHtmlPreserveFormatting(html, DisclaimerMaxLength);
    const sanitizedHtml = DOMPurify.sanitize(truncatedHtml);
    return htmlToMarkdown(sanitizedHtml);
  };

  const baselineRef = useRef(normalizeMarkdown(rawDisclaimer).trim());

  useEffect(() => {
    baselineRef.current = normalizeMarkdown(rawDisclaimer).trim();
    if (!isEditing) {
      setDebouncedDraft(baselineRef.current);
    }
  }, [rawDisclaimer, isEditing]);

  useEffect(() => {
    if (!isEditing || lastAction !== "save") return;
    setStatusMsg(null);
    const timer = setTimeout(() => {
      setStatusMsg("Disclaimer saved successfully");
      setLastAction(null);
    }, 100);

    return () => clearTimeout(timer);
  }, [isEditing, lastAction]);

  useEffect(() => {
    if (!isEditing) return;
    const container = editorRef.current;
    if (!container) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (e.key !== "Tab" || target.tagName !== "TEXTAREA") return;

      e.preventDefault();
      e.stopPropagation();

      const saveBtn = document.querySelector("#saveDisclaimerBtn") as HTMLElement | null;
      const cancelBtn = document.querySelector("#cancelDisclaimerBtn") as HTMLElement | null;
      const isSaveEnabled = !!saveBtn && !saveBtn.hasAttribute("disabled");

      if (e.shiftKey) {
        cancelBtn?.focus();
      } else if (isSaveEnabled) {
        saveBtn.focus();
      } else {
        cancelBtn?.focus();
      }
    };

    container.addEventListener("keydown", handleKeyDown, true);
    return () => container.removeEventListener("keydown", handleKeyDown, true);
  }, [isEditing]);

  const handleSave = () => {
    const contentToSave = normalizeMarkdown(debouncedDraft).trim();
    dispatch(diningReducer.UpdateDisclaimer(selectedSyncCafe!.id, contentToSave));
    setDebouncedDraft(contentToSave);
    setIsEditing(false);
    setLastAction("save");
    setStatusMsg(null);
    setIsInvalidContent(false);
  };

  const handleChange = (val?: string) => {
    const nextValue = val ?? "";
    const normalized = normalizeMarkdown(nextValue);
    setDebouncedDraft(nextValue);

    const doc = new DOMParser().parseFromString(mdToHtml(normalized), "text/html");
    const disallowedElement = doc.querySelector("script, iframe, object, embed, link, style");
    const containsInvalid = !!disallowedElement;
    setIsInvalidContent(containsInvalid);

    const baselineMd = baselineRef.current || "";
    setIsContentUnchanged(normalized === baselineMd);

    if (containsInvalid) {
      const tag = disallowedElement?.tagName.toLowerCase() || "disallowed tag";
      setStatusMsg(`HTML tag like <${tag}> is not allowed.`);
    } else {
      setStatusMsg(null);
    }
  };

  const handleCancel = () => {
    setDebouncedDraft((baselineRef.current || "").trim());
    setIsEditing(false);
    setLastAction("cancel");
    setStatusMsg(null);
    setTimeout(() => setStatusMsg("Disclaimer update has been cancelled"), 100);
  };

  const handleEdit = () => {
    const current = baselineRef.current || "";
    setIsEditing(true);
    setDebouncedDraft(current.trim());
    setStatusMsg(null);
    setIsContentUnchanged(true);
    setIsInvalidContent(false);
  };

  useEffect(() => {
    if (!isEditing) return;
    const normalizedMarkdown = normalizeMarkdown(debouncedDraft);
    const baselineMd = baselineRef.current || "";
    setIsContentUnchanged(normalizedMarkdown === baselineMd);
  }, [debouncedDraft, isEditing]);

  const disableSave = !isEditing || isContentUnchanged || isInvalidContent;
  return (
    <div>
      <Title3 as="h2" style={{ display: !isEditing ? "inline" : "block" }}>
        Add a disclaimer (optional)
      </Title3>
      {!isEditing && (
        <sup>
          <Link
            appearance="subtle"
            onClick={(event) => {
              event.preventDefault();
              handleEdit();
            }}
            style={{ marginLeft: "20px", fontSize: 14, fontWeight: 600, color: "#0F6CBD" }}
            data-testid="edit-link"
            aria-label="Edit Disclaimer"
          >
            Edit
          </Link>
        </sup>
      )}
      {!isEditing && (
        <div
          className={`wmde-markdown ${isPlaceholderText ? "placeholderText " : ""}${styles.borderedBox}`}
          tabIndex={0}
          role="region"
          aria-label="Cafe disclaimer preview"
        >
          <MarkdownPreview source={selectedCafeDisclaimer} />
        </div>
      )}

      <div
        id="manual-live-region"
        aria-live="polite"
        role="status"
        style={{ position: "absolute", left: -9999, width: 1, height: 1 }}
      >
        {statusMsg}
      </div>
      {isEditing && statusMsg && (
        <Text className={styles.errorMessage} role="status">
          {statusMsg}
        </Text>
      )}
      {isEditing && (
        <div ref={editorRef} data-color-mode="light">
          <MDEditor
            value={debouncedDraft}
            onChange={handleChange}
            preview="edit"
            commands={editorCommands}
            extraCommands={[]}
            visibleDragbar={false}
            textareaProps={{
              "aria-label": "Cafe Disclaimer Editor",
              placeholder: DisclaimerPlaceholderText,
            }}
            className={styles.textInputBigBox}
          />
        </div>
      )}
      {isEditing && (
        <div className={localStyles.wrapper}>
          <Button appearance="primary" onClick={handleSave} disabled={disableSave} id="saveDisclaimerBtn">
            Save
          </Button>
          <Button onClick={handleCancel} id="cancelDisclaimerBtn">
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
};
export default Disclaimer;
